/**
 * Supplier Sales Allocation – Simplified
 * Shows cost of goods sold per supplier + Cash on Hand / Bank Balance.
 * Click any supplier row to see the individual products.
 * Suppliers can be included/excluded via a settings panel.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RefreshCw,
  Loader2,
  Package,
  Edit,
  Save,
  Banknote,
  ChevronDown,
  ChevronRight,
  Settings,
  X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  `R ${(value || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SupplierAllocation = () => {
  const [loading, setLoading] = useState(true);
  const [allAllocations, setAllAllocations] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [dateRange, setDateRange] = useState("this_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Drilldown
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [supplierItems, setSupplierItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Supplier filter
  const [excludedSuppliers, setExcludedSuppliers] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);

  // Category filter
  const [excludedCategories, setExcludedCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [savingCatFilter, setSavingCatFilter] = useState(false);

  // Cash & Bank
  const [cashOnHand, setCashOnHand] = useState(0);
  const [bankBalance, setBankBalance] = useState(0);
  const [savingFinancials, setSavingFinancials] = useState(false);
  const [editingFinancials, setEditingFinancials] = useState(false);
  const [tempCash, setTempCash] = useState("");
  const [tempBank, setTempBank] = useState("");

  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    switch (dateRange) {
      case "today":
        setStartDate(todayStr);
        setEndDate(todayStr);
        break;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setStartDate(yesterday.toISOString().split("T")[0]);
        setEndDate(yesterday.toISOString().split("T")[0]);
        break;
      }
      case "this_week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        setStartDate(weekStart.toISOString().split("T")[0]);
        setEndDate(todayStr);
        break;
      }
      case "this_month": {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(monthStart.toISOString().split("T")[0]);
        setEndDate(todayStr);
        break;
      }
      default:
        break;
    }
  }, [dateRange]);

  useEffect(() => {
    if (startDate && endDate) fetchAllocations();
  }, [startDate, endDate]);

  useEffect(() => {
    fetchFinancialBalances();
    fetchExcludedSuppliers();
    fetchExcludedCategories();
  }, []);

  const fetchExcludedSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/supplier-allocation/excluded-suppliers`);
      setExcludedSuppliers(res.data.excluded || []);
    } catch (e) {
      console.error("Failed to load excluded suppliers:", e);
    }
  };

  const saveExcludedSuppliers = async (list) => {
    setSavingFilter(true);
    try {
      await axios.post(`${API}/supplier-allocation/excluded-suppliers`, { excluded: list });
      setExcludedSuppliers(list);
      toast.success("Supplier filter saved");
    } catch (e) {
      toast.error("Failed to save filter");
    } finally {
      setSavingFilter(false);
    }
  };

  const fetchExcludedCategories = async () => {
    try {
      const res = await axios.get(`${API}/supplier-allocation/excluded-categories`);
      setExcludedCategories(res.data.excluded || []);
    } catch (e) {
      console.error("Failed to load excluded categories:", e);
    }
  };

  const saveExcludedCategories = async (list) => {
    setSavingCatFilter(true);
    try {
      await axios.post(`${API}/supplier-allocation/excluded-categories`, { excluded: list });
      setExcludedCategories(list);
      // Refresh allocations since category filtering is server-side
      fetchAllocations();
      toast.success("Category filter saved");
    } catch (e) {
      toast.error("Failed to save category filter");
    } finally {
      setSavingCatFilter(false);
    }
  };

  const fetchAllocations = async () => {
    setLoading(true);
    setExpandedSupplier(null);
    try {
      const response = await axios.get(`${API}/supplier-allocation/sales`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setAllAllocations(response.data.allocations || []);
      setAllCategories(response.data.categories_found || []);
    } catch (error) {
      console.error("Error fetching allocations:", error);
      toast.error("Failed to load supplier sales data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Filtered + sorted allocations
  const filtered = allAllocations.filter((a) => !excludedSuppliers.includes(a.supplier));
  const filteredTotal = filtered.reduce((s, a) => s + (a.current_cycle_source ?? a.cost ?? 0), 0);
  const sorted = [...filtered].sort((a, b) => (b.cost || 0) - (a.cost || 0));

  // Cash supplier total: cost-of-sales going to suppliers we pay in physical cash
  const cashSuppliersTotal = filtered
    .filter((a) => a.is_cash)
    .reduce((s, a) => s + (a.current_cycle_source ?? a.cost ?? 0), 0);
  const cashSurplus = (cashOnHand || 0) - cashSuppliersTotal;

  // All unique supplier names (for the filter panel)
  const allSupplierNames = [...new Set(allAllocations.map((a) => a.supplier))].sort();

  const fetchFinancialBalances = async () => {
    try {
      const response = await axios.get(`${API}/supplier-allocation/financial-balances`);
      setCashOnHand(response.data.cash_on_hand || 0);
      setBankBalance(response.data.bank_balance || 0);
    } catch (error) {
      console.error("Error fetching financial balances:", error);
    }
  };

  const handleSaveFinancials = async () => {
    setSavingFinancials(true);
    try {
      await axios.post(`${API}/supplier-allocation/financial-balances`, {
        cash_on_hand: parseFloat(tempCash) || 0,
        bank_balance: parseFloat(tempBank) || 0,
      });
      setCashOnHand(parseFloat(tempCash) || 0);
      setBankBalance(parseFloat(tempBank) || 0);
      setEditingFinancials(false);
      toast.success("Financial balances updated");
    } catch (error) {
      console.error("Error saving financial balances:", error);
      toast.error("Failed to save financial balances");
    } finally {
      setSavingFinancials(false);
    }
  };

  const handleToggleSupplier = async (supplierName) => {
    if (expandedSupplier === supplierName) {
      setExpandedSupplier(null);
      setSupplierItems([]);
      return;
    }
    setExpandedSupplier(supplierName);
    setLoadingItems(true);
    try {
      const response = await axios.get(`${API}/supplier-allocation/supplier-items`, {
        params: { supplier_name: supplierName, start_date: startDate, end_date: endDate },
      });
      setSupplierItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching supplier items:", error);
      toast.error("Failed to load items");
      setSupplierItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleExclude = (supplierName) => {
    setExcludedSuppliers((prev) =>
      prev.includes(supplierName) ? prev.filter((s) => s !== supplierName) : [...prev, supplierName]
    );
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-testid="supplier-allocation-heading">
            Supplier Cost of Sales
          </h1>
          <p className="text-gray-500 mt-1">Cost price of goods sold, grouped by supplier</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowFilter(!showFilter)}
            variant={showFilter ? "default" : "outline"}
            data-testid="supplier-filter-toggle"
          >
            <Settings className="w-4 h-4 mr-2" />
            Filter Suppliers
            {(excludedSuppliers.length + excludedCategories.length) > 0 && (
              <span className="ml-2 bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded-full">
                {excludedSuppliers.length + excludedCategories.length} excluded
              </span>
            )}
          </Button>
          <Button
            onClick={() => { setRefreshing(true); fetchAllocations(); }}
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-allocations-button"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Supplier Filter Panel */}
      {showFilter && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="supplier-filter-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Include / Exclude Suppliers</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => saveExcludedSuppliers(excludedSuppliers)}
                  disabled={savingFilter}
                  data-testid="save-supplier-filter"
                >
                  {savingFilter ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowFilter(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">Unchecked suppliers will be hidden from the cost of sales table</p>
          </CardHeader>
          <CardContent>
            {allSupplierNames.length === 0 ? (
              <p className="text-sm text-gray-400">Load data first to see suppliers</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {allSupplierNames.map((name) => {
                  const isIncluded = !excludedSuppliers.includes(name);
                  return (
                    <label
                      key={name}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${
                        isIncluded ? "bg-white border-gray-200" : "bg-gray-100 border-gray-200 text-gray-400 line-through"
                      }`}
                      data-testid={`supplier-filter-${name}`}
                    >
                      <Checkbox
                        checked={isIncluded}
                        onCheckedChange={() => toggleExclude(name)}
                      />
                      <span className="truncate">{name}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Category Filter */}
            {allCategories.length > 0 && (
              <>
                <div className="border-t my-3 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Product Categories</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveExcludedCategories(excludedCategories)}
                      disabled={savingCatFilter}
                      className="h-6 text-xs"
                      data-testid="save-category-filter"
                    >
                      {savingCatFilter ? "Saving..." : "Save Categories"}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Unchecked categories will be excluded from the cost of sales</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {allCategories.map((cat) => {
                      const isIncluded = !excludedCategories.includes(cat);
                      return (
                        <label
                          key={cat}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${
                            isIncluded ? "bg-white border-gray-200" : "bg-gray-100 border-gray-200 text-gray-400 line-through"
                          }`}
                          data-testid={`category-filter-${cat}`}
                        >
                          <Checkbox
                            checked={isIncluded}
                            onCheckedChange={() =>
                              setExcludedCategories((prev) =>
                                prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
                              )
                            }
                          />
                          <span className="truncate">{cat}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Period Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Period</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40 mt-1" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-500">Date Range</label>
              <p className="text-lg font-semibold mt-1">
                {startDate && new Date(startDate).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                {startDate !== endDate && endDate && ` – ${new Date(endDate).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cash on Hand & Bank Balance */}
      <Card className="border-emerald-200 bg-emerald-50/30" data-testid="financial-overview-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-emerald-900" data-testid="financial-overview-heading">
              <Banknote className="w-5 h-5" />
              Cash &amp; Bank
            </CardTitle>
            {!editingFinancials ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTempCash(cashOnHand.toString());
                  setTempBank(bankBalance.toString());
                  setEditingFinancials(true);
                }}
                data-testid="edit-financials-button"
              >
                <Edit className="w-4 h-4 mr-2" />
                Update
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditingFinancials(false)} data-testid="cancel-financials-button">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveFinancials}
                  disabled={savingFinancials}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-financials-button"
                >
                  {savingFinancials ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-white" data-testid="cash-on-hand-section">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Cash on Hand</p>
              {editingFinancials ? (
                <Input type="number" step="0.01" min="0" value={tempCash} onChange={(e) => setTempCash(e.target.value)} className="mt-1" data-testid="cash-on-hand-input" />
              ) : (
                <p className="text-2xl font-bold text-emerald-700" data-testid="cash-on-hand-value">{formatCurrency(cashOnHand)}</p>
              )}
            </div>
            <div className="p-4 rounded-lg border bg-white" data-testid="bank-balance-section">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Bank Balance</p>
              {editingFinancials ? (
                <Input type="number" step="0.01" min="0" value={tempBank} onChange={(e) => setTempBank(e.target.value)} className="mt-1" data-testid="bank-balance-input" />
              ) : (
                <p className="text-2xl font-bold text-blue-700" data-testid="bank-balance-value">{formatCurrency(bankBalance)}</p>
              )}
            </div>
            <div className="p-4 rounded-lg border bg-emerald-100" data-testid="total-available-section">
              <p className="text-xs text-emerald-700 uppercase tracking-wide">Total Available</p>
              <p className="text-2xl font-bold text-emerald-800" data-testid="total-available-value">{formatCurrency(cashOnHand + bankBalance)}</p>
            </div>
          </div>

          {/* Cash Supplier Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="p-4 rounded-lg border bg-white" data-testid="cash-suppliers-cost-section">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Cash Suppliers Cost (Period)</p>
              <p className="text-2xl font-bold text-orange-700" data-testid="cash-suppliers-cost-value">
                {formatCurrency(cashSuppliersTotal)}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">Sales attributed to suppliers paid in physical cash.</p>
            </div>
            <div className="p-4 rounded-lg border bg-white" data-testid="cash-on-hand-compare">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Cash on Hand</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(cashOnHand)}</p>
              <p className="text-[11px] text-gray-500 mt-1">Physical cash available in till/safe.</p>
            </div>
            <div
              className={`p-4 rounded-lg border ${cashSurplus >= 0 ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}
              data-testid="cash-surplus-section"
            >
              <p className={`text-xs uppercase tracking-wide ${cashSurplus >= 0 ? "text-green-700" : "text-red-700"}`}>
                {cashSurplus >= 0 ? "Cash Surplus" : "Cash Shortfall"}
              </p>
              <p className={`text-2xl font-bold ${cashSurplus >= 0 ? "text-green-800" : "text-red-800"}`} data-testid="cash-surplus-value">
                {cashSurplus >= 0 ? "+" : ""}{formatCurrency(cashSurplus)}
              </p>
              <p className="text-[11px] text-gray-600 mt-1">
                {cashSurplus >= 0
                  ? "You have enough cash to cover cash suppliers."
                  : "Not enough cash on hand for cash suppliers."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supplier Cost Table */}
      <Card data-testid="supplier-cost-table-card">
        <CardHeader>
          <CardTitle>Cost of Sales by Supplier</CardTitle>
          <p className="text-sm text-gray-500">Click a supplier to see item breakdown</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">No sales in this period</p>
              <p className="text-sm">Sales will appear here automatically</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Cost of Sales</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((item, idx) => {
                  const cost = item.current_cycle_source ?? item.cost ?? 0;
                  const pct = filteredTotal > 0 ? ((cost / filteredTotal) * 100).toFixed(1) : "0.0";
                  const isExpanded = expandedSupplier === item.supplier;
                  return (
                    <>
                      <TableRow
                        key={idx}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleToggleSupplier(item.supplier)}
                        data-testid={`supplier-row-${idx}`}
                      >
                        <TableCell className="pr-0">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{item.supplier}</span>
                            {item.is_cash && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-300">CASH</span>
                            )}
                            {item.is_cod && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-300">COD</span>
                            )}
                            {item.is_consignment && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-300">Consignment</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(cost)}</TableCell>
                        <TableCell className="text-right text-gray-500">{pct}%</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${idx}-detail`}>
                          <TableCell colSpan={4} className="p-0 bg-gray-50">
                            {loadingItems ? (
                              <div className="flex items-center justify-center py-6">
                                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                                <span className="text-sm text-gray-500">Loading items...</span>
                              </div>
                            ) : supplierItems.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center py-4">No items found</p>
                            ) : (
                              <div className="px-4 py-2">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase border-b">
                                      <th className="text-left py-2 pr-2">PLU</th>
                                      <th className="text-left py-2">Product</th>
                                      <th className="text-right py-2">Qty</th>
                                      <th className="text-right py-2">Unit Cost</th>
                                      <th className="text-right py-2 pl-2">Total Cost</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {supplierItems.map((si, sidx) => (
                                      <tr key={sidx} className="border-b border-gray-100" data-testid={`supplier-item-${sidx}`}>
                                        <td className="py-1.5 pr-2 font-mono text-gray-600">{si.plu_code || "-"}</td>
                                        <td className="py-1.5">{si.name}</td>
                                        <td className="py-1.5 text-right">{Number.isInteger(si.quantity) ? si.quantity : si.quantity.toFixed(2)}</td>
                                        <td className="py-1.5 text-right text-gray-600">{formatCurrency(si.unit_cost)}</td>
                                        <td className="py-1.5 text-right font-semibold pl-2">{formatCurrency(si.total_cost)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
                <TableRow className="bg-gray-100 font-bold" data-testid="supplier-totals-row">
                  <TableCell></TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right text-lg text-green-700">{formatCurrency(filteredTotal)}</TableCell>
                  <TableCell className="text-right text-gray-500">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierAllocation;
